/**
 * The composer's effort control, right after the model: how hard the model
 * thinks before it answers. Drawn only for a model that offers a choice; the
 * slider it opens has a stop for each effort that model offers.
 *
 * The chip shows the effort the model will run at (`nearestEffort`), which
 * is not always the one the chat holds. Any pick also becomes the account's
 * default, as a model pick does.
 *
 * On a phone there is no chip: the model's names the effort too, and the
 * model menu ends with this popover's body (`EffortPanel`, `useEffortPick`).
 */

import { useLayoutEffect, useRef } from 'react';
import { patchSettings, type Effort } from '../../api';
import { useApp } from '../../state';
import { Menu, Ph, useDismiss } from '../ui';
import { EFFORT_LABELS, nearestEffort } from './effort';

export function EffortMenu({
  model,
  value,
  onPick,
  open,
  onToggle,
  onClose
}: {
  model: string;
  value: Effort;
  onPick: (effort: Effort) => void;
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const { catalogue } = useApp();
  const ref = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const pick = useEffortPick(value, onPick);
  useDismiss(ref, open, onClose);

  // As the model menu does: on a phone the popover moves right until it is
  // on screen.
  useLayoutEffect(() => {
    const el = menuRef.current;
    if (!el) return;
    const over = 16 - el.getBoundingClientRect().left;
    if (over > 0) el.style.right = `${-over}px`;
  }, [open]);

  const offered = catalogue?.models.find((m) => m.id === model)?.efforts ?? [];
  const shown = nearestEffort(value, offered);
  if (!shown) return null;

  return (
    <span ref={ref} style={{ position: 'relative', display: 'flex', flex: 'none' }}>
      <button
        type="button"
        className="bare hov-tint"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`Effort: ${EFFORT_LABELS[shown]}`}
        title="Effort"
        onClick={onToggle}
        style={{
          height: 32,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '0 8px',
          borderRadius: 'var(--radius-sm)',
          fontSize: 12.5,
          color: 'var(--text-body)',
          whiteSpace: 'nowrap'
        }}
      >
        {EFFORT_LABELS[shown]}
        <Ph name="caret-down" size={11} color="var(--text-faint)" />
      </button>

      {open ? (
        <Menu ref={menuRef} role="dialog" style={{ right: 0, bottom: 'calc(100% + 6px)', width: 248, padding: '12px 14px 12px' }}>
          <EffortPanel offered={offered} value={shown} onChange={pick} />
        </Menu>
      ) : null}
    </span>
  );
}

/**
 * The slider's picks. The popover stays open while the thumb moves, so the
 * label follows it. Every stop is a pick; the default is saved one at a time,
 * the latest next.
 */
export function useEffortPick(value: Effort, onPick: (effort: Effort) => void): (effort: Effort) => void {
  const { patchMe } = useApp();
  const wanted = useRef<Effort | null>(null);
  const saving = useRef<Promise<void>>(Promise.resolve());

  return (effort: Effort) => {
    if (effort === value) return;
    onPick(effort);
    wanted.current = effort;
    saving.current = saving.current.then(async () => {
      if (wanted.current !== effort) return;
      try {
        const settings = await patchSettings({ defaultEffort: effort });
        patchMe((x) => ({ ...x, settings }));
      } catch {
        // As with the model: the chat has it, only the next one's default
        // did not stick.
      }
    });
  };
}

/** The popover's body: the effort by name, its track, and which end is which. */
export function EffortPanel({
  offered,
  value,
  onChange
}: {
  offered: readonly Effort[];
  value: Effort;
  onChange: (effort: Effort) => void;
}) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
        <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>Effort</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-strong)' }}>{EFFORT_LABELS[value]}</span>
      </div>
      <EffortTrack offered={offered} value={value} onChange={onChange} />
      <div
        aria-hidden="true"
        style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, fontSize: 12, color: 'var(--text-faint)' }}
      >
        <span>Faster</span>
        <span>Smarter</span>
      </div>
    </>
  );
}

/** The track's height, and so its ends' radius. */
const TRACK = 22;

/**
 * One stop per effort the model offers, lowest on the left. A native range
 * input sits over the drawn track, so dragging, clicking and the arrow keys
 * all work and a screen reader hears the label.
 */
export function EffortTrack({
  offered,
  value,
  onChange
}: {
  offered: readonly Effort[];
  value: Effort;
  onChange: (effort: Effort) => void;
}) {
  const at = Math.max(0, offered.indexOf(value));
  const last = offered.length - 1;
  // A stop's centre, from the track's inner edge: the ends sit half a track in.
  const left = (i: number) => (last ? `calc(${TRACK / 2}px + (100% - ${TRACK}px) * ${i / last})` : '50%');

  return (
    <div style={{ position: 'relative', height: TRACK }}>
      <div
        aria-hidden="true"
        style={{ position: 'absolute', inset: 0, borderRadius: TRACK / 2, background: 'var(--border-subtle)' }}
      >
        {offered.map((e, i) => (
          <span
            key={e}
            style={{
              position: 'absolute',
              top: '50%',
              left: left(i),
              width: 4,
              height: 4,
              borderRadius: 2,
              background: 'var(--text-faint)',
              transform: 'translate(-50%, -50%)'
            }}
          />
        ))}
        <span
          style={{
            position: 'absolute',
            top: '50%',
            left: left(at),
            width: 14,
            height: TRACK - 4,
            borderRadius: 7,
            background: 'var(--text-strong)',
            boxShadow: 'var(--shadow-sm)',
            transform: 'translate(-50%, -50%)',
            transition: 'left 120ms ease'
          }}
        />
      </div>
      <input
        type="range"
        min={0}
        max={last}
        step={1}
        value={at}
        aria-label="Effort"
        aria-valuetext={EFFORT_LABELS[value]}
        onChange={(e) => onChange(offered[Number(e.target.value)] ?? value)}
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', margin: 0, opacity: 0, cursor: 'pointer' }}
      />
    </div>
  );
}
